/**
 * The routing framework (STEP 3) — one `createBrowserRouter` tree
 * composing every guard (Maintenance -> Protected/Guest -> Role/
 * Permission where needed), every layout (AppLayout/AuthLayout), and
 * every page, all lazy-loaded with a `Suspense` boundary per branch so
 * one page's chunk never blocks another's.
 *
 * Feature modules (Stage F1+) add their own route subtrees as
 * children of the `ProtectedRoute` element below — this file's own
 * shape never needs to change, only grow.
 */
import { Suspense, type JSX } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { PatientWorkstationRedirect } from '@features/patient/pages/PatientWorkstationRedirect';
import { AppLayout } from '@layouts/AppLayout';
import { AuthLayout } from '@layouts/AuthLayout';
import { ProtectedRoute, GuestRoute, PermissionRoute, AnyPermissionRoute, CorrectionApproverRoute, MaintenanceGuard } from './guards';
import { PageLoader } from '@components/feedback/PageLoader';
import { DischargePermissions, MrdPermissions, IpAdmissionPermissions, PatientPermissions, ALL_APPOINTMENT_PERMISSIONS, FinancialPermissions, ClinicalPermissions, PlatformPermissions, FinanceAccountingPermissions, MasterDataPermissions, ProcurementPermissions, InventoryPermissions, PricingPermissions, PharmacyBillingPermissions, LabPermissions, CashManagementPermissions, AnalyticsPermissions, HrPermissions } from '@nexao/contracts';
import {
  LoginPage,
  DashboardPage,
  NotFoundPage,
  ServerErrorPage,
  ForbiddenPage,
  MaintenancePage,
  PatientSearchPage,
  PatientRegistrationPage,
  PatientProfilePage,
  RegistrationConfigurationPage,
  AppointmentWorkstationPage,
  BillingWorkstationPage,
  OpBillingWorkstationPage,
  UniversalBillingWorkstationPage,
  IpBillingWorkstationPage,
  IpDashboardPage,
  IpLookupPage,
  IpDrugEntriesPage,
  IpRegistrationPage,
  DischargeDashboardPage,
  DischargeWorklistPage,
  DischargeDeskPage,
  DischargeLabReportsPage,
  DischargeRegisterPage,
  MrdDashboardPage,
  MrdInboxPage,
  MrdDeficienciesPage,
  MrdCodingPage,
  MrdIndexPage,
  MrdCodeListPage,
  MrdFileTrackerPage,
  MrdArchivePage,
  MrdRetentionPage,
  MrdRequestsPage,
  MrdCertificatesPage,
  MrdIssueLogPage,
  MrdBirthsPage,
  MrdDeathsPage,
  MrdMlcPage,
  MrdOutcomesPage,
  MrdNotifiablePage,
  MrdStatisticsPage,
  MrdSettingsPage,
  HrAttendancePage,
  HrRecruitmentPage,
  HrOnboardingPage,
  HrTrainingPage,
  FinanceConfigurationWorkstationPage,
  PatientBillingWorkspacePage,
  OutstandingPage,
  IpBillingPage,
  LabBillingPage,
  LabCataloguePage,
  LabWorkspacePage,
  LabWorkflowPage,
  LabOrdersPage,
  LabLookupPage,
  OpBillsEnquirePage,
  LabTestsReportPage,
  IpBillEnquirePage,
  MainCounterPage,
  CounterSettlementPage,
  CounterVerificationPage,
  CounterReconciliationPage,
  CounterReportsPage,
  CounterCorrectionsReportPage,
  ReportsAnalyticsPage,
  FrontDeskDashboardPage,
  LabPlaceOrderPage,
  LabCollectionPage,
  LabResultsPage,
  LabVerificationPage,
  LabReportsPage,
  LabOrderRecordPage,
  RadiologyBillingPage,
  ClinicalDashboardPage,
  EncounterWorkspacePage,
  ClinicalDocumentsPage,
  ClinicalDocumentViewPage,
  ClinicalOrdersPage,
  ClinicalTimelinePage,
  ClinicalApprovalsPage,
  AdministrationHubPage,
  CorrectionWorkflowPage,
  ModificationsCorrectionPage,
  DownloadPage,
  FinanceHubPage,
  PharmacyMasterHubPage,
  PharmacyProcurementHubPage,
  GeneralStoresHubPage,
  InsuranceHubPage,
  PricingHubPage,
  PharmacyBillingWorkstationPage,
  MyProfilePage,
} from './lazy-pages';

function withSuspense(element: JSX.Element): JSX.Element {
  return <Suspense fallback={<PageLoader />}>{element}</Suspense>;
}

const router = createBrowserRouter([
  {
    element: <MaintenanceGuard />,
    children: [
      { path: '/maintenance', element: withSuspense(<MaintenancePage />) },
      {
        element: <GuestRoute />,
        children: [
          {
            element: <AuthLayout />,
            children: [
              { path: '/login', element: withSuspense(<LoginPage />) },
              // Disaster-recovery exposure — same LoginPage component,
              // distinguished by pathname (see LoginPage.tsx's
              // isRecoveryRoute) to force environment: 'recovery' instead
              // of the Production default.
              { path: '/recovered/login', element: withSuspense(<LoginPage />) },
            ],
          },
        ],
      },
      {
        element: <ProtectedRoute />,
        children: [
          {
            element: <AppLayout />,
            children: [
              { path: '/', element: withSuspense(<DashboardPage />) },
              // ADR-033 — bare sibling route, no PermissionRoute wrapper (same
              // as / above): every authenticated user has a profile, so
              // there's no lowest-common-permission bar to gate at the route
              // level — each tab inside gates its own actions individually.
              { path: '/profile', element: withSuspense(<MyProfilePage />) },
              // Receipt corrections (desk, 2026-10-05): a bare route like /profile, because a user selected as a verifier or
              // approver need not hold ANY permission — the selection decides, on the server, what each person may see and do.
              { path: '/corrections', element: withSuspense(<CorrectionWorkflowPage />) },
              // Modifications (desk, 2026-10-07): ONLY a user selected as a correction approver — not a permission, not a role, so not
              // the SuperAdmin either. `CorrectionApproverRoute` asks the server who is selected; the server checks again per request.
              {
                element: <CorrectionApproverRoute />,
                children: [{ path: '/modifications/correction', element: withSuspense(<ModificationsCorrectionPage />) }],
              },
              // Download (owner, 2026-10-22): Excel workbooks of the hospital's records, for administrators only. The route is the permission
              // `data-export:read`; the server checks it again on every request, and checks the run / download grants for the two actions.
              {
                element: <PermissionRoute requires={[PlatformPermissions.DATA_EXPORT_READ]} />,
                children: [{ path: '/download', element: withSuspense(<DownloadPage />) }],
              },
              { path: '/403', element: withSuspense(<ForbiddenPage />) },
              // Feature-module route subtrees (Stage F1+: /patients, /appointments,
              // /financial, /clinical, ...) are added here as sibling entries —
              // each wrapped in its own PermissionRoute/RoleRoute where needed.
              {
                element: <PermissionRoute requires={[PatientPermissions.PATIENT_SEARCH]} />,
                children: [
                  { path: '/patients', element: withSuspense(<PatientSearchPage />) },
                  { path: '/patients/:patientId', element: withSuspense(<PatientProfilePage />) },
                ],
              },
              {
                element: <PermissionRoute requires={[PatientPermissions.PATIENT_REGISTER]} />,
                children: [
                  { path: '/patients/register', element: withSuspense(<PatientRegistrationPage />) },
                  /*
                    THE OLD WORKSTATION FORWARDS — 2026-09-07. Leaving it
                    RENDERING is how a bookmark kept serving the superseded
                    screen after the sidebar had already moved; the tab is
                    translated because the two pages use the same ids for
                    different tabs. See `PatientWorkstationRedirect`.
                  */
                  { path: '/patients/workstation', element: <PatientWorkstationRedirect /> },
                ],
              },
              {
                element: <PermissionRoute requires={[PatientPermissions.PATIENT_REGISTRATION_CONFIGURATION_VIEW]} />,
                children: [{ path: '/patients/registration-configuration', element: withSuspense(<RegistrationConfigurationPage />) }],
              },
              {
                // Enterprise Appointment Workstation — single page, single route,
                // one top-level `TabsPanel` (Dashboard/Book/Check-in/Queue/
                // Waitlist/Lookup/Doctor Schedule), matching the Pharmacy
                // Billing Workstation's own shell pattern. Any one of the 8 real
                // AppointmentPermissions grants entry (the old 6 sub-routes each
                // required a different one); each tab still gates itself by its
                // own specific permission via `TabsPanel`'s own `requires`, so
                // nobody sees an action their role can't actually perform. The 6
                // old sub-routes redirect to the matching `?tab=` deep link now
                // (previously just the bare page) — zero breaking changes for
                // existing links/bookmarks/keyboard shortcuts (e.g. Front Desk's
                // F4), and a bookmark now actually lands on the right tab.
                element: <AnyPermissionRoute anyOf={ALL_APPOINTMENT_PERMISSIONS} />,
                children: [
                  { path: '/appointments', element: withSuspense(<AppointmentWorkstationPage />) },
                  { path: '/appointments/calendar', element: <Navigate to="/appointments?tab=book" replace /> },
                  { path: '/appointments/book', element: <Navigate to="/appointments?tab=book" replace /> },
                  { path: '/appointments/lookup', element: <Navigate to="/appointments?tab=lookup" replace /> },
                  { path: '/appointments/check-in', element: <Navigate to="/appointments?tab=check-in" replace /> },
                  { path: '/appointments/queue', element: <Navigate to="/appointments?tab=queue" replace /> },
                  { path: '/appointments/doctor-schedule', element: <Navigate to="/appointments?tab=doctor-schedule" replace /> },
                ],
              },
              {
                // Enterprise Billing Workstation — single page, single route,
                // one top-level TabsPanel (Dashboard/Cashier/Packages &
                // Pricing/Refunds & Credit Notes), matching the Pharmacy
                // Billing Workstation's own shell pattern (see Appointment's
                // own TabsPanel rebuild for the same reasoning). Any one of
                // the 3 real billing-workstation permissions grants entry;
                // each tab still gates itself by its own specific permission
                // via TabsPanel's own `requires`. The 3 old sub-routes
                // (cashier/packages/refunds) redirect to the matching
                // `?tab=` deep link instead of the bare page now.
                element: (
                  <AnyPermissionRoute
                    anyOf={[
                      FinancialPermissions.FINANCIAL_PAYMENT_PROCESS,
                      FinancialPermissions.FINANCIAL_PACKAGE_MANAGE,
                      FinancialPermissions.FINANCIAL_REFUND_PROCESS,
                    ]}
                  />
                ),
                children: [
                  { path: '/billing', element: withSuspense(<BillingWorkstationPage />) },
                  // The ADR-062 production workstation lives at its OWN route.
                  // `/billing` is left alone deliberately — it is in daily use,
                  // and swapping it under people is not a decision a build makes
                  // for them. The two share Cashier, Packages & Pricing and
                  // Refunds & Credit Notes by mounting the SAME components, so
                  // there is one implementation behind both routes, not a copy.
                  { path: '/billing/counter', element: withSuspense(<UniversalBillingWorkstationPage />) },
                  { path: '/op-billing', element: withSuspense(<OpBillingWorkstationPage />) },
                  { path: '/finance/configuration', element: withSuspense(<FinanceConfigurationWorkstationPage />) },
                  { path: '/billing/cashier', element: <Navigate to="/billing?tab=cashier" replace /> },
                  { path: '/billing/packages', element: <Navigate to="/billing?tab=packages" replace /> },
                  { path: '/billing/refunds', element: <Navigate to="/billing?tab=refunds" replace /> },
                ],
              },
              {
                // ADR-085 Phase 14 — the IP Billing Workstation gets its OWN
                // permission group rather than joining the financial one above.
                // IP billing is a different desk with a different roster: a
                // cashier who settles OP bills has no business opening an
                // admission's charge ledger, and `ip:admission:view` is the
                // permission that actually says so. Entry needs only the read
                // permission; every action inside the workstation gates itself
                // on its own specific `ip:*` permission, and is HIDDEN rather
                // than disabled when absent.
                element: <PermissionRoute requires={[IpAdmissionPermissions.IP_ADMISSION_VIEW]} />,
                children: [{ path: '/ip-billing', element: withSuspense(<IpBillingWorkstationPage />) }],
              },
              {
                /*
                 * THE IP COMMAND CENTRE — ADR-085 Phase 12's payload, which had
                 * no screen until now.
                 *
                 * `ip:dashboard:view` is its own permission and the same one the
                 * endpoint carries, so the route and the API agree: whoever can
                 * reach the page can always load it. Every CARD inside is then
                 * filtered again by the server against its own permission, so
                 * holding this grants the PAGE, never its contents.
                 */
                element: <PermissionRoute requires={[IpAdmissionPermissions.IP_DASHBOARD_VIEW]} />,
                children: [{ path: '/ip/dashboard', element: withSuspense(<IpDashboardPage />) }],
              },
              {
                /*
                 * IP LOOKUP — the read-only reference desk.
                 *
                 * `ip:admission:view`, deliberately NOT the admitting grant.
                 * The stay-lookup that existed lived on the admitting screen
                 * behind `ip:admission:admit`, so a ward clerk or a supervisor
                 * who may never admit anyone could not look a stay up at all —
                 * and the only alternative was the IP Billing workstation,
                 * which is gated on the charge permissions and exists to ACT on
                 * money. Reading a record is neither of those acts.
                 *
                 * Each SECTION inside re-checks its own grant
                 * (`ip:charge:view`, `ip:settlement:view`,
                 * `ip:interim-bill:view`) and says so rather than 403-ing, so
                 * holding this permission grants the page, never its contents.
                 */
                element: <PermissionRoute requires={[IpAdmissionPermissions.IP_ADMISSION_VIEW]} />,
                children: [{ path: '/ip/lookup', element: withSuspense(<IpLookupPage />) }],
              },
              {
                /*
                 * IP DRUG ENTRIES (desk, 2026-09-25) — pharmacy bills entered against
                 * ADMITTED stays, with refunds and a register. Entry needs only
                 * `ip:charge:view`; entering a bill needs `ip:charge:capture` and a
                 * refund `ip:charge:reverse`, and each is HIDDEN rather than disabled
                 * inside the page when the user does not hold it.
                 */
                element: <PermissionRoute requires={[IpAdmissionPermissions.IP_CHARGE_VIEW]} />,
                children: [{ path: '/ip/drug-entries', element: withSuspense(<IpDrugEntriesPage />) }],
              },
              /*
               * DISCHARGE MODULE (owner, 2026-10-23). The module code opens the sidebar section (Administration → Roles → Modules); each
               * route is then gated on its own permission, and the server checks the same key on every request. Entry to the desk, the
               * worklist, the dashboard and the register is `discharge:desk:view`; the combined lab report has its own, because it is
               * one patient's clinical documents and not the laboratory's worklist. The acts a desk performs keep the `ip:*` permission
               * they have always carried, and are hidden inside the page from anyone who lacks it.
               */
              { path: '/discharge', element: <Navigate to="/discharge/dashboard" replace /> },
              {
                element: <PermissionRoute requires={[DischargePermissions.DISCHARGE_DESK_VIEW]} />,
                children: [
                  { path: '/discharge/dashboard', element: withSuspense(<DischargeDashboardPage />) },
                  { path: '/discharge/worklist', element: withSuspense(<DischargeWorklistPage />) },
                  { path: '/discharge/desk', element: withSuspense(<DischargeDeskPage />) },
                  { path: '/discharge/register', element: withSuspense(<DischargeRegisterPage />) },
                ],
              },
              {
                element: <PermissionRoute requires={[DischargePermissions.DISCHARGE_LAB_REPORT_VIEW]} />,
                children: [{ path: '/discharge/lab-reports', element: withSuspense(<DischargeLabReportsPage />) }],
              },
              /*
               * MRD MODULE (owner, 2026-11-02). The module code opens the sidebar section (Administration → Roles → Modules); each route is then
               * gated on its own permission, and the server checks the same key on every request. Entry to every page is `mrd:desk:view`;
               * Settings (the code list) needs `mrd:coding:manage`. The acts a page offers (receive a file, code a stay, write a register, print a
               * certificate) carry their own keys and are hidden inside the page from anyone who lacks them — the server refuses them regardless.
               */
              { path: '/mrd', element: <Navigate to="/mrd/dashboard" replace /> },
              {
                element: <PermissionRoute requires={[MrdPermissions.MRD_DESK_VIEW]} />,
                children: [
                  { path: '/mrd/dashboard', element: withSuspense(<MrdDashboardPage />) },
                  { path: '/mrd/inbox', element: withSuspense(<MrdInboxPage />) },
                  { path: '/mrd/deficiencies', element: withSuspense(<MrdDeficienciesPage />) },
                  { path: '/mrd/coding', element: withSuspense(<MrdCodingPage />) },
                  { path: '/mrd/index', element: withSuspense(<MrdIndexPage />) },
                  { path: '/mrd/code-list', element: withSuspense(<MrdCodeListPage />) },
                  { path: '/mrd/file-tracker', element: withSuspense(<MrdFileTrackerPage />) },
                  { path: '/mrd/archive', element: withSuspense(<MrdArchivePage />) },
                  { path: '/mrd/retention', element: withSuspense(<MrdRetentionPage />) },
                  { path: '/mrd/requests', element: withSuspense(<MrdRequestsPage />) },
                  { path: '/mrd/medical-certificates', element: withSuspense(<MrdCertificatesPage />) },
                  { path: '/mrd/issue-log', element: withSuspense(<MrdIssueLogPage />) },
                  { path: '/mrd/births', element: withSuspense(<MrdBirthsPage />) },
                  { path: '/mrd/deaths', element: withSuspense(<MrdDeathsPage />) },
                  { path: '/mrd/mlc', element: withSuspense(<MrdMlcPage />) },
                  { path: '/mrd/outcomes', element: withSuspense(<MrdOutcomesPage />) },
                  { path: '/mrd/notifiable', element: withSuspense(<MrdNotifiablePage />) },
                  { path: '/mrd/statistics', element: withSuspense(<MrdStatisticsPage />) },
                ],
              },
              {
                element: <PermissionRoute requires={[MrdPermissions.MRD_CODING_MANAGE]} />,
                children: [{ path: '/mrd/settings', element: withSuspense(<MrdSettingsPage />) }],
              },
              {
                // HR — Attendance (Phase 1A). Entry needs only the narrow
                // `hr:attendance:view`; the SCOPE of what the list returns
                // (self / team / all) is decided server-side by whatever else
                // the caller holds. `/hr/attendance/me` is reachable by any
                // logged-in employee and self-checks inside the page.
                element: <PermissionRoute requires={[HrPermissions.HR_ATTENDANCE_VIEW]} />,
                children: [{ path: '/hr/attendance', element: withSuspense(<HrAttendancePage />) }],
              },
              {
                // HR — Recruitment (Phase 2A: job requisition). Same posture as
                // Attendance above: gated on the narrow `view`, scope (self vs.
                // tenant-wide) resolved server-side from whatever else the
                // caller holds. `/hr/requisitions/me` is reachable by any
                // logged-in employee and self-checks inside the page.
                element: <PermissionRoute requires={[HrPermissions.HR_REQUISITION_VIEW]} />,
                children: [{ path: '/hr/recruitment', element: withSuspense(<HrRecruitmentPage />) }],
              },
              {
                // HR — Onboarding (Phase 3). Starts from a candidate who
                // ACCEPTED an offer (Phase 2D's own terminal stage) — never
                // creates the Employee/User row itself, see the page's own
                // header comment.
                element: <PermissionRoute requires={[HrPermissions.HR_ONBOARDING_VIEW]} />,
                children: [{ path: '/hr/onboarding', element: withSuspense(<HrOnboardingPage />) }],
              },
              {
                // HR — Training & Development (Phase 4). Catalogue seeded
                // with the master brief's own named mandatory-training
                // examples, editable via the Catalogue tab.
                element: <PermissionRoute requires={[HrPermissions.HR_TRAINING_VIEW]} />,
                children: [{ path: '/hr/training', element: withSuspense(<HrTrainingPage />) }],
              },
              {
                // The IP Registration Desk. Entry needs ADMIT, not VIEW: this
                // screen exists to CREATE admissions, and admitting someone
                // who should not be admitted is not undone by a read
                // permission. Its UHID Lookup tab is read-only, but anyone who
                // only needs to look up a stay has the IP Billing workstation
                // for that — this route is the admitting desk.
                element: <PermissionRoute requires={[IpAdmissionPermissions.IP_ADMISSION_ADMIT]} />,
                children: [{ path: '/ip/register', element: withSuspense(<IpRegistrationPage />) }],
              },
              {
                element: <PermissionRoute requires={[FinancialPermissions.FINANCIAL_BILLING_COMPUTE]} />,
                children: [{ path: '/billing/workspace', element: withSuspense(<PatientBillingWorkspacePage />) }],
              },
              {
                element: <PermissionRoute requires={[FinancialPermissions.FINANCIAL_ADVANCE_DEPOSIT_MANAGE]} />,
                children: [{ path: '/billing/outstanding', element: withSuspense(<OutstandingPage />) }],
              },
              {
                // IP / Laboratory / Radiology BILLING — dedicated screens where
                // sidebar placeholders used to sit. Same permission as the OP
                // billing workstation: they are the same desk's work, split by
                // service line rather than by who is allowed to do it.
                element: <PermissionRoute requires={[FinancialPermissions.FINANCIAL_PAYMENT_PROCESS]} />,
                children: [
                  { path: '/billing/ip', element: withSuspense(<IpBillingPage />) },
                  { path: '/billing/laboratory', element: withSuspense(<LabBillingPage />) },
                  { path: '/billing/radiology', element: withSuspense(<RadiologyBillingPage />) },
                  /*
                    THE LABORATORY MODULE'S OWN DOOR TO THE SAME SCREEN, `ipOnly`
                    (desk, 2026-09-21: "under laboratory module there is a billing
                    screen... not required OP... keep op under frontdesk... not
                    under laboratory module"). A technologist billing from inside
                    the module is taking IP orders, not raising OP bills — see
                    `LabBillingPage`'s own doc comment. Same permission as the
                    other three: it is the same desk's work reached through a
                    different door, not a different right.
                  */
                  { path: '/laboratory/billing', element: withSuspense(<LabBillingPage ipOnly />) },
                ],
              },
              {
                /**
                 * Phase 5J — defect D-1 CLOSED.
                 *
                 * These two routes sat in the billing group above, guarded by
                 * `financial:payment:process`. A BILLING permission gated the
                 * clinical catalogue: a pathologist without cashier rights
                 * could not open the Clinical Mapping Workstation, while any
                 * cashier could. That was harmless while only SuperAdmin held
                 * any lab grant; the moment real laboratory roles exist it is
                 * the thing standing between 251 signature-ready tests and the
                 * people authorised to sign them.
                 *
                 * `lab:master:view` is the permission the SERVER already
                 * requires on every route these pages call, so the guard now
                 * matches what the API enforces rather than contradicting it.
                 */
                element: <PermissionRoute requires={[LabPermissions.LAB_MASTER_VIEW]} />,
                children: [
                  // ADR-087 Phase 2 — the LAB master-data workspace.
                  { path: '/laboratory/tests', element: withSuspense(<LabCataloguePage />) },
                  // ADR-087 Phase 3 — the tabbed LAB master workspace, and the
                  // home of the Phase 5I Clinical Mapping Workstation.
                  { path: '/laboratory', element: withSuspense(<LabWorkspacePage />) },
                ],
              },
              {
                /**
                 * ADR-088 Phase 10 — the LAB workflow workstation.
                 *
                 * Guarded by `lab:workflow:view`, NOT by a billing permission.
                 * The master-data workspace above still sits behind
                 * `FINANCIAL_PAYMENT_PROCESS`, which is a pre-existing defect
                 * reported separately — the clinical workflow must not inherit
                 * it, because collecting specimens and entering results have
                 * nothing to do with processing a payment.
                 */
                element: <PermissionRoute requires={[LabPermissions.LAB_WORKFLOW_VIEW]} />,
                children: [
                  /**
                   * ADR-097 — SIX STAGES, SIX ROUTES.
                   *
                   * `navigation-registry.ts` had always registered these as six
                   * independent sidebar leaves and then pointed all six at one
                   * page with a different `?tab=`. The routes now match the
                   * navigation, `/laboratory/workflow` redirects, and each page
                   * owns its own query string — which is what stops one stage's
                   * status filter following the operator into the next.
                   *
                   * `/orders/new` is declared BEFORE `/orders/:accession`, or
                   * placing an order would be read as an accession of "new".
                   */
                  { path: '/laboratory/orders', element: withSuspense(<LabOrdersPage />) },
                  /*
                    Desk, 2026-08-26. Declared BEFORE `/orders/:accession` is
                    irrelevant here — it is not under /orders — but it IS under
                    the workflow guard on purpose: Lookup reads the same orders
                    the worklists read, so it answers to the same permission.
                  */
                  { path: '/laboratory/lookup', element: withSuspense(<LabLookupPage />) },
                  { path: '/laboratory/op-bills-enquire', element: withSuspense(<OpBillsEnquirePage />) },
                  { path: '/laboratory/ip-bill-enquire', element: withSuspense(<IpBillEnquirePage />) },
                  /* LAB TESTS REPORT (desk, 2026-11-03) — the laboratory's own monthly sheet; the amount column is the server's call, by permission. */
                  { path: '/laboratory/tests-report', element: withSuspense(<LabTestsReportPage />) },
                  { path: '/laboratory/orders/new', element: withSuspense(<LabPlaceOrderPage />) },
                  { path: '/laboratory/orders/:accession', element: withSuspense(<LabOrderRecordPage />) },
                  { path: '/laboratory/collection', element: withSuspense(<LabCollectionPage />) },
                  { path: '/laboratory/results', element: withSuspense(<LabResultsPage />) },
                  { path: '/laboratory/verification', element: withSuspense(<LabVerificationPage />) },
                  { path: '/laboratory/reports', element: withSuspense(<LabReportsPage />) },
                  { path: '/laboratory/workflow', element: withSuspense(<LabWorkflowPage />) },
                ],
              },
              /*
                ── THE MAIN COUNTER (desk, 2026-08-28) ────────────────────────
                "create Main Counter workstation which used for all counter
                mother and should do Cash and Bank transaction reconciallation
                with settlement shift chnage process."

                Its OWN guard, on the cash-management summary grant rather than
                on `financial:payment:process`. Taking money and overseeing every
                counter's takings are different authorities: a cashier bills at
                one drawer; this screen reads all of them and decides whether a
                shift's count is accepted.

                Three routes rather than three tabs, so a controller can be sent
                "the reconciliation screen" as a link and Back behaves.
              */
              {
                element: <PermissionRoute requires={[CashManagementPermissions.CASH_SUMMARY_READ]} />,
                children: [
                  { path: '/counter', element: withSuspense(<MainCounterPage />) },
                  { path: '/counter/settlement', element: withSuspense(<CounterSettlementPage />) },
                  /* The line-by-line pass under settlement — see
                     `CounterVerificationPage` for why it had no route until now. */
                  { path: '/counter/verification', element: withSuspense(<CounterVerificationPage />) },
                  { path: '/counter/reconciliation', element: withSuspense(<CounterReconciliationPage />) },
                  { path: '/counter/reports', element: withSuspense(<CounterReportsPage />) },
                  /* The Corrections report (desk, 2026-10-12) — every correction request, read-only, behind the same Main Counter permission. */
                  { path: '/counter/reports/corrections', element: withSuspense(<CounterCorrectionsReportPage />) },
                ],
              },
              {
                /*
                  REPORTS & ANALYTICS — one route, three reports as tabs.

                  ONE ROUTE AND NOT THREE, which is the opposite of the choice
                  the Main Counter above makes, and for the opposite reason:
                  those three screens are separate authorities read by different
                  people, while these three are one period looked at three ways
                  and are read together at a handover. The tab lives in
                  `?tab=`, so a report is still linkable and Back still moves
                  between them.

                  Gated on `analytics:report:view` alone. Export is a separate
                  grant, checked inside the page where it hides one button
                  rather than the whole screen — a reader who may not extract a
                  file may still read the report.
                */
                element: <PermissionRoute requires={[AnalyticsPermissions.ANALYTICS_REPORT_VIEW]} />,
                children: [
                  { path: '/reports', element: withSuspense(<ReportsAnalyticsPage />) },
                  /*
                    THE FRONT DESK'S LANDING PAGE — the same report body, under a
                    route its own sidebar section points at. Gated on the same
                    permission by the parent `PermissionRoute`, because it reads
                    the same endpoint: a second route to one report must not be a
                    second answer to who may read it.
                  */
                  { path: '/front-desk/dashboard', element: withSuspense(<FrontDeskDashboardPage />) },
                ],
              },
              {
                element: <PermissionRoute requires={[ClinicalPermissions.CLINICAL_VIEW]} />,
                children: [
                  { path: '/clinical', element: withSuspense(<ClinicalDashboardPage />) },
                  { path: '/clinical/timeline', element: withSuspense(<ClinicalTimelinePage />) },
                  { path: '/clinical/approvals', element: withSuspense(<ClinicalApprovalsPage />) },
                  // Discharge Summary, Phase 4C — View + Print. Gated the same as the
                  // backend's own `GET /clinical/documents/:documentId`: CLINICAL_VIEW,
                  // not CLINICAL_CREATE (that group below owns the editor at the
                  // shorter, static `/clinical/documents` path).
                  { path: '/clinical/documents/:documentId', element: withSuspense(<ClinicalDocumentViewPage />) },
                ],
              },
              {
                element: <PermissionRoute requires={[ClinicalPermissions.CLINICAL_CREATE]} />,
                children: [
                  { path: '/clinical/workspace', element: withSuspense(<EncounterWorkspacePage />) },
                  { path: '/clinical/documents', element: withSuspense(<ClinicalDocumentsPage />) },
                ],
              },
              {
                element: <PermissionRoute requires={[ClinicalPermissions.CLINICAL_ORDER_CREATE]} />,
                children: [{ path: '/clinical/orders', element: withSuspense(<ClinicalOrdersPage />) }],
              },
              {
                // The hub page itself further gates each of its 9 tabs individually via
                // TabsPanel's own per-tab `requires` — this route-level gate only needs
                // the lowest bar any tab requires (Users), matching nav-config.ts's own entry.
                element: <PermissionRoute requires={[PlatformPermissions.USER_READ]} />,
                children: [{ path: '/administration', element: withSuspense(<AdministrationHubPage />) }],
              },
              {
                // Same pattern as Administration above — FinanceHubPage's own 4
                // tabs each gate individually, this only needs the lowest bar.
                element: <PermissionRoute requires={[FinanceAccountingPermissions.FINANCE_LEDGER_READ]} />,
                children: [{ path: '/finance', element: withSuspense(<FinanceHubPage />) }],
              },
              {
                // Same pattern as Finance above — PharmacyMasterHubPage's
                // own 4 tabs each gate individually, this only needs the
                // lowest bar (Stage 14, Phase 1).
                element: <PermissionRoute requires={[MasterDataPermissions.DRUG_MASTER_READ]} />,
                children: [{ path: '/pharmacy', element: withSuspense(<PharmacyMasterHubPage />) }],
              },
              {
                // Same pattern as Pharmacy above — PharmacyProcurementHubPage's
                // own 2 tabs each gate individually, this only needs the
                // lowest bar (Stage 14, Phase 2, Step 2a).
                element: <PermissionRoute requires={[ProcurementPermissions.PROCUREMENT_PO_READ]} />,
                children: [{ path: '/pharmacy-procurement', element: withSuspense(<PharmacyProcurementHubPage />) }],
              },
              {
                /*
                 * General Stores — the consumables/non-drug workstation.
                 *
                 * Gated on `stock-batch:read`, the LOWEST bar of its own tabs,
                 * exactly as the hubs above are: each tab gates itself, and a
                 * route-level gate stricter than the loosest tab would hide a
                 * screen the user is entitled to. Deliberately NOT gated on
                 * `item-master:read` — a storekeeper who may see stock but not
                 * maintain the catalogue must still reach the workstation.
                 */
                element: <PermissionRoute requires={[InventoryPermissions.STOCK_BATCH_READ]} />,
                children: [{ path: '/general-stores', element: withSuspense(<GeneralStoresHubPage />) }],
              },
              {
                /*
                  INSURANCE (ADR-098). Gated on `ip:insurance:view`, the lowest bar
                  of the hub's own tabs — each tab gates itself, and a route gate
                  stricter than the loosest tab would hide a screen the user may
                  open. The write actions inside are gated server-side on
                  `ip:insurance:edit`.
                */
                element: <PermissionRoute requires={[IpAdmissionPermissions.IP_INSURANCE_VIEW]} />,
                children: [{ path: '/insurance', element: withSuspense(<InsuranceHubPage />) }],
              },
              {
                // Same pattern — PricingHubPage's own 3 tabs each gate
                // individually, this only needs the lowest bar (Stage 14,
                // Phase 3, Step 3a).
                element: <PermissionRoute requires={[PricingPermissions.PRICE_LIST_READ]} />,
                children: [{ path: '/pricing', element: withSuspense(<PricingHubPage />) }],
              },
              {
                element: <PermissionRoute requires={[PharmacyBillingPermissions.PHARMACY_SALE_READ]} />,
                children: [
                  // Stage 15 Phase 14 cutover — the old AG-Grid hub/sale
                  // page is retired; `/pharmacy-billing` (old deep links)
                  // now also renders the Workstation.
                  { path: '/pharmacy-billing', element: withSuspense(<PharmacyBillingWorkstationPage />) },
                  { path: '/pharmacy-billing/workstation', element: withSuspense(<PharmacyBillingWorkstationPage />) },
                ],
              },
            ],
          },
        ],
      },
      { path: '/500', element: withSuspense(<ServerErrorPage />) },
      { path: '*', element: withSuspense(<NotFoundPage />) },
    ],
  },
]);

export function AppRouter(): JSX.Element {
  return <RouterProvider router={router} />;
}
